import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ChevronDown, ChevronUp } from "lucide-react";
import { validateLogin } from "../../services/validate-login";
import Header from "../../components/common/Header";
import Footer from "../../components/common/Footer";
import { v4 as uuidv4 } from "uuid";
import { getFormattedDate } from "../../utils/dateFormat";
import { confirmResponseService } from "../../services/ondcService";
import Loader from "../../components/common/Loader";

function ExpansionPanel({
  title,
  children,
  defaultOpen = false,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-gray-300 rounded-lg">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex justify-between items-center px-4 py-3 text-left font-medium text-gray-700 hover:bg-gray-100"
      >
        {title}
        {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </button>

      {isOpen && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

function Form() {
  const [lendersOffer, setLendersOffer] = useState<any>();
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  useEffect(() => {
    async function getStep() {
      if(Number(localStorage.getItem("step")) >= 13){} else{ navigate("/personal-loan") }
    }
    getStep();
  }, []);
  useEffect(() => {
    async function fetchOffers() {
      localStorage.setItem("step", "14")
      try {
        const data = {
          transaction_id: localStorage.getItem("transaction_id"),
          message_id: localStorage.getItem("message_id"),
        };
        const response = await confirmResponseService(data);
        if (response.success) {
          setLendersOffer(response.message.response[0])
        }
      } catch (err) {
        console.error("Error fetching offers:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchOffers();
  }, []);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#f5f9ff]">
        <Loader />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-[#f5f9ff] px-4">
      {/* Hero Section */}
      <div className="flex flex-col md:flex-row max-w-5xl w-full bg-white rounded-2xl shadow-lg overflow-hidden">
        {/* Left side Image (fixed) */}
        <div className="basis-full md:basis-2/5 flex items-center justify-center p-6 bg-gray-50 md:h-[calc(100vh-100px)] md:sticky md:top-0">
          <img
            src="svg/img1.svg"
            alt="Working professional"
            className="w-full max-w-xs md:max-w-none object-contain"
          />
        </div>

        {/* Right side Form (scrollable) */}
        <div className="basis-full md:basis-3/5 flex flex-col p-8 bg-white relative md:h-[calc(100vh-100px)] md:overflow-y-auto">
          {/* Step Number */}
          <div className="absolute top-4 right-4 bg-blue-100 text-blue-700 text-xs font-semibold px-3 py-1 rounded-full shadow-sm">
            Step 14
          </div>
          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto">
            <h2 className="text-xl font-semibold mb-6">LOAN Details</h2>
            <p className="text-sm text-gray-600 mb-4">
              Loan application is submitted
            </p>

            {/* Extra Fields */}
            <div className="space-y-6">
              {/* Text Field */}
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Loan ID
                </label>
                <div className="flex items-center border border-gray-300 rounded-lg">
                  <input
                    type="text"
                    value={lendersOffer.order_id}
                    readOnly
                    disabled
                    className="w-full px-4 py-2 rounded-r-lg focus:ring-2 focus:ring-blue-500 outline-none border-0"
                  />
                </div>
              </div>

              {/* Duration Field */}
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Loan Status
                </label>
                <input
                  type="text"
                  value={lendersOffer.fulfillments}
                  readOnly
                  disabled
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>
                           {/* Text Field */}
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Payable Amount
                </label>
                <div className="flex items-center border border-gray-300 rounded-lg">
                  <span className="px-3 text-gray-600">Rs.</span>
                  <input
                    type="text"
                    value={lendersOffer.quote_price}
                    readOnly
                    disabled
                    className="w-full px-4 py-2 rounded-r-lg focus:ring-2 focus:ring-blue-500 outline-none border-0"
                  />
                </div>
              </div>

              {/* Duration Field */}
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Loan Amount
                </label>
                <div className="flex items-center border border-gray-300 rounded-lg">
                  <span className="px-3 text-gray-600">Rs.</span>
                  <input
                    type="text"
                    value={lendersOffer.loan_amount}
                    readOnly
                    disabled
                    className="w-full px-4 py-2 rounded-r-lg focus:ring-2 focus:ring-blue-500 outline-none border-0"
                  />
                </div>
              </div>

              {/* Accordion Panels */}
              <ExpansionPanel title="Loan Breakup">
                {lendersOffer?.quote_breakup?.map((breakup: any, index: number) => (
                <div key={index} className="mb-4">
                  <label className="block text-sm text-gray-600 mb-1">
                    {breakup.title}
                  </label>
                  <input
                    readOnly
                    disabled
                    type="text"
                    value={breakup.price.value || ""}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
              ))}
              </ExpansionPanel>

              {/* Accordion Panels */}
              <ExpansionPanel title="Loan Info">
                {lendersOffer?.item_tags?.list.map((tag: any, index: number) => (
                <div key={index} className="mb-4">
                  <label className="block text-sm text-gray-600 mb-1">
                    {tag.descriptor.name}
                  </label>
                  <input
                    readOnly
                    disabled
                    type="text"
                    value={tag.value || ""}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
              ))}
              </ExpansionPanel>

              {/* Accordion Panels */}
              <ExpansionPanel title="LSP Info">
                {lendersOffer?.provider_lsp_info?.list.map((tag: any, index: number) => (
                <div key={index} className="mb-4">
                  <label className="block text-sm text-gray-600 mb-1">
                    {tag.descriptor.name}
                  </label>
                  <input
                    readOnly
                    disabled
                    type="text"
                    value={tag.value || ""}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
              ))}
              </ExpansionPanel>

              {/* Accordion Panels */}
              <ExpansionPanel title="GRO Info">
                {lendersOffer?.gro_info?.list.map((tag: any, index: number) => (
                <div key={index} className="mb-4">
                  <label className="block text-sm text-gray-600 mb-1">
                    {tag.descriptor.name}
                  </label>
                  <input
                    readOnly
                    disabled
                    type="text"
                    value={tag.value || ""}
                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
              ))}
              </ExpansionPanel>

              {/* Accordion Panels */}
              <ExpansionPanel title="EMI Schedule">
                <div className="flex items-center justify-between text-sm text-gray-700">
                  <div className="flex-1 text-center">
                    <p className="font-bold">Due Date</p>
                  </div>

                  <div className="border-l border-gray-400 h-5"></div>

                  <div className="flex-1 text-center">
                    <p className="font-bold">EMI Amount</p>
                  </div>

                  <div className="border-l border-gray-400 h-5"></div>

                  <div className="flex-1 text-center">
                    <p className="font-bold">Status</p>
                  </div>
                </div>
                {lendersOffer?.payments.map((tag: any, index: number) => (
                <div className="flex items-center justify-between text-sm text-gray-700">
                  <div className="flex-1 text-center">
                    <p className="text-gray-600">{new Date(tag.time.range.end).toISOString().split("T")[0]}</p>
                  </div>

                  <div className="border-l border-gray-400 h-5"></div>

                  <div className="flex-1 text-center">
                    <p className="text-gray-600">Rs. {tag.params.amount}</p>
                  </div>

                  <div className="border-l border-gray-400 h-5"></div>

                  <div className="flex-1 text-center">
                    <p className="text-gray-600">{tag.status}</p>
                  </div>
                </div>
              ))}
              </ExpansionPanel>
            </div>
          </div>

          {/* Next Button pinned at bottom */}
          <div className="mt-6">
            <button 
            className="w-full bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition flex items-center justify-center gap-2"
            onClick={() => navigate("/dashboard")}
            >
              Home
            </button>
          </div>
        </div>
      </div>

      {/* Footer Stats */}
      <Footer />
    </div>
  );
}

export default function Step13LoanConfirmation() {
  const navigate = useNavigate();

  useEffect(() => {
    validateLogin(navigate);
  }, [navigate]);

  return (
    <div className="flex flex-col h-screen overflow-y-auto">
      <div className="flex-shrink-0">
        <Header title="" />
      </div>

      {/* Main Content scrollable */}
      <main className="flex-1 overflow-y-auto">
          <Form />
      </main>
    </div>
  );
}
